import React, { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/store";
import { ButtonsContainer, ConfirmContainer } from "./styles";
import ModalContentWithCloseButton from "../common/ModalContentWithCloseButton";
import {
  AlertState,
  ConfirmState,
  OptionsState,
  removeSounds,
  selectOption,
  setAlert,
  setConfirm,
  setOptions,
} from "../../reducers/alerts";
import Button from "../common/Button";
import { ButtonTypes, ButtonVariants } from "../common/Button/constants";
import LoadingOverlay from "../LoadingOverlay";
import Modal from "../common/Modal";
import { usePlaySound } from "../../utils/hooks/sounds";

export function Confirm() {
  const dispatch = useAppDispatch();
  const {
    confirm: confirmState,
    confirmMessage,
    confirmText,
    cancelText,
  } = useAppSelector((state) => state.alerts);

  const cancel = () => {
    dispatch(setConfirm({ confirm: ConfirmState.OFF, message: null }));
  };

  const confirm = useCallback(() => {
    dispatch(
      setConfirm({
        confirm: ConfirmState.CONFIRMED,
        message: confirmMessage,
      }),
    );
  }, [confirmMessage, dispatch]);

  return (
    <>
      <LoadingOverlay
        loading={confirmState == ConfirmState.CONFIRMED}
        text={"Confirming"}
      />
      <Modal
        isOpen={confirmState == ConfirmState.WAITING_FOR_CONFIRMATION}
        onRequestClose={cancel}
      >
        <ModalContentWithCloseButton closeModal={cancel}>
          <ConfirmContainer>
            <div>{confirmMessage}</div>
            <ButtonsContainer>
              <Button
                label={cancelText}
                onClick={cancel}
                variant={ButtonVariants.SECONDARY}
              />
              <form onSubmit={confirm}>
                <Button
                  label={confirmText}
                  type={ButtonTypes.SUBMIT}
                  autoFocus
                />
              </form>
            </ButtonsContainer>
          </ConfirmContainer>
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}

export function Alert() {
  const dispatch = useAppDispatch();
  const { alert: alertState, alertMessage } = useAppSelector(
    (state) => state.alerts,
  );

  const acknowledge = useCallback(() => {
    dispatch(setAlert({ alert: AlertState.OFF, message: null }));
  }, [dispatch]);

  return (
    <Modal
      isOpen={alertState == AlertState.ALERTING}
      onRequestClose={acknowledge}
    >
      <ModalContentWithCloseButton closeModal={acknowledge}>
        <ConfirmContainer>
          <div>{alertMessage}</div>
          <form onSubmit={acknowledge}>
            <Button label={"Ok"} type={ButtonTypes.SUBMIT} autoFocus />
          </form>
        </ConfirmContainer>
      </ModalContentWithCloseButton>
    </Modal>
  );
}

export function Sounds() {
  const soundBank = usePlaySound();
  const dispatch = useAppDispatch();
  const soundsToPlay = useAppSelector((state) => state.alerts.soundsToPlay);

  useEffect(() => {
    if (soundsToPlay.length > 0) {
      soundsToPlay.forEach((soundToPlay) => {
        soundBank[soundToPlay.soundType]();
      });
      dispatch(
        removeSounds({
          idsToRemove: soundsToPlay.map((soundToPlay) => {
            return soundToPlay.id;
          }),
        }),
      );
    }
  }, [dispatch, soundBank, soundsToPlay]);

  return null;
}

export function Options() {
  const dispatch = useAppDispatch();
  const {
    options: optionsState,
    optionsMessage,
    optionItems,
  } = useAppSelector((state) => state.alerts);

  const cancel = () => {
    dispatch(
      setOptions({
        options: OptionsState.OFF,
        optionsMessage: null,
        optionItems: [],
      }),
    );
  };

  const select = useCallback(
    (index: number) => {
      dispatch(selectOption({ index }));
    },
    [dispatch],
  );
  return (
    <>
      <LoadingOverlay
        loading={optionsState == OptionsState.SELECTED}
        text={"Confirming"}
      />
      <Modal
        isOpen={optionsState == OptionsState.WAITING_FOR_SELECTION}
        onRequestClose={cancel}
      >
        <ModalContentWithCloseButton closeModal={cancel}>
          <ConfirmContainer>
            <div>{optionsMessage}</div>
            <ButtonsContainer>
              {optionItems.map((option, index) => (
                <Button
                  key={`${option.text}-${index}`}
                  label={option.text}
                  onClick={() => select(index)}
                  variant={option.variant}
                />
              ))}
            </ButtonsContainer>
            <Button
              label={"Cancel"}
              onClick={cancel}
              variant={ButtonVariants.SECONDARY}
            />
          </ConfirmContainer>
        </ModalContentWithCloseButton>
      </Modal>
    </>
  );
}
